import { ChartActionConfig, ChartData } from '../../types/dashboard';
import { ChartExpandedModal } from './ChartExpandedModal';
import { BarChart3, CheckCircle, EyeOff, Eye, Image as ImageIcon, Info, Maximize2, MoreHorizontal, Download, RotateCcw, FoldHorizontal, UnfoldHorizontal, Table as TableIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { copyChartAsImage } from '@/utils/chartExport';
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import React, { useState, useRef, useEffect, memo } from 'react';
import { useGlobalToast } from '@/components/UI/toaster';
import { useEditorStore } from '../../store/editor-store';
import { ChartRenderer, chartFromTemplate, formatNumberFull, formatNumberShort, stripChartPrefix, FALLBACK_PALETTE, getChartNumericFields, getChartValueRange, getNiceTickValues, getDisplayName, isChartActionEnabled, getCartesianChartGrid, getXAxisFieldKey } from '../charts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { FinanceV2Section } from './FinanceV2Section';
import { FinanceV2DataTable } from './FinanceV2DataTable';

const TOP_X_VALUES = 15;
const HORIZONTAL_BAR_TOP_X_VALUES = 5;
const DEFAULT_TOP_N_OPTIONS = [3, 5, 10, 20, 'all'] as const;
const FROZEN_AXIS_LEFT_WIDTH = 72;
const FROZEN_AXIS_RIGHT_WIDTH = 72;
const HORIZONTAL_BAR_LABEL_GUTTER_MIN = 132;
const HORIZONTAL_BAR_LABEL_GUTTER_MAX = 260;
const HORIZONTAL_BAR_LABEL_GUTTER_MAX_FULL = 320;
const CHART_INLINE_ACTIONS_WIDTH = 760;
const CHART_INLINE_CONTROLS_WIDTH = 980;

export type ChartSortMode = 'none' | 'high_to_low' | 'low_to_high';
type ChartTopNValue = number | 'all';

const CHART_SORT_OPTIONS: Array<{ value: ChartSortMode; label: string }> = [
  { value: 'none', label: 'Default order' },
  { value: 'high_to_low', label: 'High to low' },
  { value: 'low_to_high', label: 'Low to high' },
];

// Repo-2 branding pass: local chart surface classes keep this styling scoped to Smart Insights.
const chartSectionClassName =
  "transition-all duration-300 hover:border-[#6E3450]/35 hover:shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]";
const chartIconButtonClassName =
  "h-7 w-7 rounded-[8px] border border-transparent p-0 text-[#7B6B72] transition-colors hover:border-[#EDE6E1] hover:bg-[#FAF7F1] hover:text-[#6E3450]";
const chartActiveIconButtonClassName =
  "h-7 w-7 rounded-[8px] border border-[#6E3450]/20 bg-[#f5e9ee] p-0 text-[#6E3450] transition-colors hover:bg-[#f5e9ee]/80";
const chartCompactButtonClassName =
  "h-7 rounded-[8px] border border-[#EDE6E1] bg-white px-2 text-[11px] font-medium text-[#7B6B72] transition-colors hover:border-[#6E3450]/35 hover:bg-[#FAF7F1] hover:text-[#6E3450]";
const chartMenuContentClassName =
  "rounded-[10px] border border-[#EDE6E1] bg-white p-1 text-[#3F3338] shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]";
const chartMenuItemClassName =
  "cursor-pointer rounded-[8px] text-[12px] text-[#3F3338] transition-colors focus:bg-[#FAF7F1] focus:text-[#6E3450]";

/** Take first N items in original array order only. No sort is applied. */
function takeFirstInOriginalOrder<T>(arr: T[], n: number): T[] {
  return arr.slice(0, n);
}

/** Sort chart data by primary value (sum of numeric fields per row). Returns new array. */
function sortChartData(
  chart: ChartData,
  data: Record<string, unknown>[],
  mode: ChartSortMode
): Record<string, unknown>[] {
  if (mode === 'none' || !data.length) return data;
  const fields = getChartNumericFields(chart, data);
  const getRowValue = (row: Record<string, unknown>) =>
    fields.reduce((sum, k) => sum + Number(row[k] ?? row.value ?? 0), 0);
  const sorted = [...data].sort((a, b) => getRowValue(a) - getRowValue(b));
  return mode === 'high_to_low' ? sorted.reverse() : sorted;
}

function getBackendSortOrder(chart: ChartData): 'asc' | 'desc' {
  return String(chart.sort_order || '').toLowerCase() === 'asc' ? 'asc' : 'desc';
}

function getBackendSortField(chart: ChartData, data: Record<string, unknown>[]): string | null {
  const explicit = typeof chart.sort_by === 'string' && chart.sort_by.length > 0 ? chart.sort_by : null;
  if (explicit && data.some((row) => row[explicit] !== null && row[explicit] !== undefined && row[explicit] !== '')) {
    return explicit;
  }

  const xField = getXAxisFieldKey(chart);
  const graphId = String((chart as any).graph_id || '').toLowerCase();
  const title = String(chart.title || '').toLowerCase();
  const isProblemChart = (
    graphId.includes('problem')
    || title.includes('problem category')
    || title.includes('problem reason')
    || xField === 'Problem_Category'
    || xField === 'Problem_Reason'
  );
  if (isProblemChart && data.some((row) => row.Recorded_Events !== null && row.Recorded_Events !== undefined && row.Recorded_Events !== '')) {
    return 'Recorded_Events';
  }

  return null;
}

function sortChartDataByField(
  data: Record<string, unknown>[],
  field: string | null,
  order: 'asc' | 'desc'
): Record<string, unknown>[] {
  if (!field || data.length <= 1) return data;
  const direction = order === 'asc' ? 1 : -1;
  return [...data].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];
    const aNum = Number(aValue);
    const bNum = Number(bValue);
    const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
    if (bothNumeric) return (aNum - bNum) * direction;
    return String(aValue ?? '').localeCompare(String(bValue ?? '')) * direction;
  });
}

function getChartCardKey(chart: ChartData, index: number): string {
  // Stable identity for per-chart UI state (expanded data tables, full
  // width, hidden, sort/topN modes). Must NOT depend on ``index``: during
  // streaming the chart array grows and reorders, so an index-based key
  // shifts every time a new chart_ready frame lands. The pruning effect
  // below would then wipe state for charts whose key changed (even
  // though the chart itself is still present), and React would unmount
  // and remount the card — closing any open popup or modal triggered
  // from inside the chart card.
  //
  // Prefer a backend-assigned stable id (graph_id / chart.id) when the
  // chart was emitted with one. Fall back to title+type, which is
  // stable across streaming updates as long as the curator doesn't
  // rename the same chart mid-flight (it doesn't).
  const stableId = (chart as any).graph_id || (chart as any).id;
  if (stableId) return `id::${String(stableId)}`;
  return `${chart.title}::${chart.type}`;
}

function getChartMinWidth(chart: ChartData, dataLength: number, isFullWidth: boolean): number {
  const densityTypes = new Set(['bar', 'line', 'area', 'multi_column_bar', 'stacked_bar', 'dual_axis_line', 'histogram', 'waterfall']);
  const wideChartTypes = new Set(['horizontal_bar', 'diverging_bar', 'candlestick', 'parallel_coordinates']);
  const safeDataLength = Math.max(1, dataLength);

  if (wideChartTypes.has(chart.type)) {
    const baseWidth = isFullWidth ? 760 : 560;
    const perPointWidth = safeDataLength <= 4 ? 86 : 64;
    return Math.max(baseWidth, Math.min(2200, safeDataLength * perPointWidth));
  }

  if (densityTypes.has(chart.type)) {
    const perPointWidth = safeDataLength <= 4 ? 110 : safeDataLength <= 8 ? 82 : 58;
    const baseWidth = isFullWidth
      ? safeDataLength <= 4 ? 620 : 860
      : safeDataLength <= 4 ? 360 : safeDataLength <= 8 ? 500 : 620;
    return Math.max(baseWidth, Math.min(2200, safeDataLength * perPointWidth));
  }

  if (chart.type === 'pie' || chart.type === 'donut' || chart.type === 'sunburst' || chart.type === 'treemap') {
    return isFullWidth ? 760 : 520;
  }

  return isFullWidth ? 860 : 560;
}

function horizontalBarCategoryLabel(chart: ChartData, row: Record<string, unknown>): string {
  const xField = getXAxisFieldKey(chart);
  if (xField && row[xField] != null && row[xField] !== '') {
    return String(row[xField]);
  }
  for (const key of ['Plant_Line_SKU', 'Problem_Reason', 'Component', 'name', 'Name', 'label']) {
    if (row[key] != null && row[key] !== '') return String(row[key]);
  }
  const firstText = Object.values(row).find((value) => value != null && value !== '' && Number.isNaN(Number(value)));
  return firstText == null ? '' : String(firstText);
}

function getHorizontalBarLabelGutter(
  chart: ChartData,
  data: Record<string, unknown>[],
  isFullWidth: boolean
): number {
  if (chart.type !== 'horizontal_bar' && chart.type !== 'diverging_bar') {
    return HORIZONTAL_BAR_LABEL_GUTTER_MIN;
  }
  const longest = data.reduce(
    (max, row) => Math.max(max, horizontalBarCategoryLabel(chart, row).length),
    0
  );
  const visibleChars = Math.min(longest, isFullWidth ? 42 : 34);
  const desired = 36 + visibleChars * 5.8;
  const max = isFullWidth ? HORIZONTAL_BAR_LABEL_GUTTER_MAX_FULL : HORIZONTAL_BAR_LABEL_GUTTER_MAX;
  return Math.round(Math.max(HORIZONTAL_BAR_LABEL_GUTTER_MIN, Math.min(max, desired)));
}

// Hard caps. Chart height is decoupled from container width — going
// full-width must not also grow the chart vertically. The viewport
// caps below are tuned to fit one chart comfortably in view without
// scrolling the page; charts with more rows than fit at this density
// scroll INSIDE their container (see ``overflowY`` on the wrappers
// and MatrixChart's internal scroll).
const CHART_VIEWPORT_MAX = 520;          // any chart's visible window
const HORIZONTAL_BAR_RENDER_MAX = 720;   // horizontal bars can grow but not unbounded
const HORIZONTAL_BAR_ROW_PX = 36;        // tighter than 42 — matches matrix row pitch

function getChartViewportHeight(chart: ChartData, _dataLength: number, _isFullWidth: boolean): number {
  const dataLength = Math.max(1, _dataLength);
  if (chart.type === 'horizontal_bar' || chart.type === 'diverging_bar') {
    if (dataLength <= 4) return 320;
    return Math.min(CHART_VIEWPORT_MAX, 420);
  }

  // Matrix / heatmap family — viewport is fixed; the matrix itself
  // scrolls internally when row count exceeds what fits. Width is
  // irrelevant here on purpose.
  if (['matrix', 'heatmap', 'sensitivity_matrix', 'correlation_matrix'].includes(chart.type)) {
    return Math.min(CHART_VIEWPORT_MAX, 480);
  }

  // Cartesian charts — width-independent. Slight bump with data
  // length to keep tick labels legible, but capped well below the
  // square-aspect zone.
  if (['bar', 'line', 'area', 'multi_column_bar', 'stacked_bar', 'dual_axis_line', 'dual_axis_bar_line', 'histogram', 'waterfall'].includes(chart.type)) {
    if (dataLength <= 4) return 320;
    if (dataLength <= 8) return 360;
    return 400;
  }

  return 360;
}

function getChartRenderHeight(chart: ChartData, dataLength: number, isFullWidth: boolean): number {
  if (chart.type === 'horizontal_bar' || chart.type === 'diverging_bar') {
    const baseHeight = getChartViewportHeight(chart, dataLength, isFullWidth);
    // Many-row horizontal bars need a taller render surface so each
    // bar gets ~``HORIZONTAL_BAR_ROW_PX`` of real estate, but cap so
    // wider charts don't blow the viewport vertically too. Container
    // scrolls when render > viewport.
    return Math.max(baseHeight, Math.min(HORIZONTAL_BAR_RENDER_MAX, 120 + dataLength * HORIZONTAL_BAR_ROW_PX));
  }

  if (['matrix', 'heatmap', 'sensitivity_matrix', 'correlation_matrix'].includes(chart.type)) {
    // Render = viewport. MatrixChart handles internal row scrolling
    // when grid rows overflow the fixed canvas.
    return getChartViewportHeight(chart, dataLength, isFullWidth);
  }

  return getChartViewportHeight(chart, dataLength, isFullWidth);
}

function supportsFrozenVerticalAxis(chartType: ChartData['type']): boolean {
  return ['bar', 'line', 'area', 'multi_column_bar', 'stacked_bar', 'histogram', 'waterfall', 'dual_axis_line'].includes(chartType);
}

function supportsFrozenHorizontalAxis(chartType: ChartData['type']): boolean {
  return ['horizontal_bar', 'diverging_bar'].includes(chartType);
}

function getAdjustedChartRenderHeight(
  chart: ChartData,
  data: Record<string, unknown>[],
  isFullWidth: boolean
): number {
  const baseHeight = getChartRenderHeight(chart, data.length, isFullWidth);
  if (!supportsFrozenVerticalAxis(chart.type)) return baseHeight;

  const fields = getChartNumericFields(chart, data);
  const grid = getCartesianChartGrid(chart, data, fields);
  return Math.max(baseHeight, grid.top + grid.bottom + grid.plotMinHeight);
}

function normalizeTopNValue(value: unknown): ChartTopNValue | null {
  if (value === 'all') return 'all';
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function getChartTopNOptions(chart: ChartData, dataLength: number): ChartTopNValue[] {
  const topNAction = chart.template?.actions?.find((action: ChartActionConfig) => action.id === 'top_n');
  const rawValues = Array.isArray(topNAction?.options?.values)
    ? topNAction?.options?.values
    : DEFAULT_TOP_N_OPTIONS;
  const normalized = rawValues
    .map(normalizeTopNValue)
    .filter((value): value is ChartTopNValue => value !== null);
  const unique = Array.from(new Set(normalized));
  const values = unique.length > 0 ? unique : [...DEFAULT_TOP_N_OPTIONS];
  const dynamicValues = values.filter((value) => value === 'all' || value < dataLength);

  return dynamicValues.includes('all') ? dynamicValues : [...dynamicValues, 'all'];
}

function formatTopNLabel(value: ChartTopNValue): string {
  return value === 'all' ? 'All' : `Top ${value}`;
}

function formatSortModeLabel(mode: ChartSortMode): string {
  if (mode === 'high_to_low') return 'High to low';
  if (mode === 'low_to_high') return 'Low to high';
  return 'Original order';
}

function formatChartTypeLabel(type: ChartData['type']): string {
  return type.replace(/_/g, ' ');
}

const Spinner = ({ className = '' }: { className?: string }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => {
      setWidth(Math.round(element.getBoundingClientRect().width));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

function ChartToolbarSurface({ children }: { children: (width: number | null) => React.ReactNode }) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();

  return (
    <div ref={ref} className="flex w-full flex-wrap items-center gap-2">
      {children(width)}
    </div>
  );
}

interface ChartSectionProps {
  charts: ChartData[];
  botMessageId?: string;
  /** Click-handler for the chart info icon — opens the unified
   *  LineagePanel (same UX as the KPI lineage panel). */
  onShowChartLineage?: (chart: ChartData) => void;
}

function ChartSectionComponent({ charts, botMessageId, onShowChartLineage }: ChartSectionProps) {
  const { success } = useGlobalToast();
  const [copiedChartId, setCopiedChartId] = useState<string | null>(null);
  const [expandedDataCharts, setExpandedDataCharts] = useState<Set<string>>(new Set());
  const [fullWidthCharts, setFullWidthCharts] = useState<Set<string>>(new Set());
  const [hiddenCharts, setHiddenCharts] = useState<Set<string>>(new Set());
  const [showDataTables, setShowDataTables] = useState<Set<string>>(new Set());
  const [chartSortMode, setChartSortMode] = useState<Record<string, ChartSortMode>>({});
  const [chartTopNMode, setChartTopNMode] = useState<Record<string, ChartTopNValue>>({});
  const [copyingChartIndex, setCopyingChartIndex] = useState<number | null>(null);
  const [copyingTableIndex, setCopyingTableIndex] = useState<number | null>(null);
  const [focusTargetChartKey, setFocusTargetChartKey] = useState<string | null>(null);
  const [expandedChartContext, setExpandedChartContext] = useState<{
    chart: ChartData;
    data: Record<string, unknown>[];
    originalRowCount: number;
    appliedTopN: ChartTopNValue;
    sortLabel: string;
  } | null>(null);
  const cardRefs = useRef(new Map<string, HTMLElement | null>());

  const chartColors = useEditorStore((state) => state.getChartColors()) || FALLBACK_PALETTE;

  useEffect(() => {
    if (!focusTargetChartKey) return;
    const target = cardRefs.current.get(focusTargetChartKey);
    if (!target) return;

    const timer = window.setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      target.focus({ preventScroll: true });
      setFocusTargetChartKey(null);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [focusTargetChartKey]);

  useEffect(() => {
    const validChartKeys = new Set(charts.map((chart, index) => getChartCardKey(chart, index)));
    const pruneSet = (prev: Set<string>) => new Set([...prev].filter((key) => validChartKeys.has(key)));

    setExpandedDataCharts(pruneSet);
    setFullWidthCharts(pruneSet);
    setHiddenCharts(pruneSet);
    setShowDataTables(pruneSet);
    setChartSortMode((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => validChartKeys.has(key)))
    );
    setChartTopNMode((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => validChartKeys.has(key))) as Record<string, ChartTopNValue>
    );
  }, [charts]);

  const setCardRef = (chartKey: string) => (node: HTMLElement | null) => {
    cardRefs.current.set(chartKey, node);
  };

  if (!charts || charts.length === 0) return null;

  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      textarea.style.left = '-1000px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const buildTSV = (rows: Array<Record<string, unknown>>, columns: string[]) => {
    const sanitize = (val: unknown) => {
      if (val == null) return '';
      const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
      return str.replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
    };
    const header = columns.map(sanitize).join('\t');
    const body = rows.map((row) => columns.map((c) => sanitize(row?.[c])).join('\t')).join('\n');
    return body ? `${header}\n${body}` : header;
  };

  const buildCSV = (rows: Array<Record<string, unknown>>, columns: string[]) => {
    const escapeCell = (val: unknown) => {
      if (val == null) return '';
      const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
      return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = columns.map(escapeCell).join(',');
    const body = rows.map((row) => columns.map((c) => escapeCell(row?.[c])).join(',')).join('\n');
    return body ? `${header}\n${body}` : header;
  };

  const downloadTextFile = (filename: string, content: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = (chart: ChartData) => {
    const displayData = chart.fullData && chart.fullData.length > 0 ? chart.fullData : chart.data;
    if (!displayData || displayData.length === 0) {
      success("No data", "There is no chart data to export.");
      return;
    }
    const columns = Object.keys(displayData[0] || {});
    const safeTitle = chart.title.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'chart_data';
    downloadTextFile(`${safeTitle}.csv`, buildCSV(displayData, columns), 'text/csv;charset=utf-8');
    success("CSV downloaded", `"${chart.title}" data has been exported.`);
  };

  const handleChartInfoClick = (chart: ChartData) => {
    // Single lineage surface: the unified LineagePanel right-rail drawer.
    onShowChartLineage?.(chart);
  };

  const handleCopyChart = async (chart: ChartData, chartIndex: number) => {
    setCopyingChartIndex(chartIndex);
    const chartElement = document.querySelector(`[data-chart-id="chart-${chartIndex}"]`) as HTMLElement;
    if (!chartElement) {
      success("Error", "Could not find chart element to copy.");
      setCopyingChartIndex(null);
      return;
    }
    const rect = chartElement.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.left >= 0
      && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
    if (!isVisible) {
      chartElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      await new Promise(resolve => setTimeout(resolve, 300));
    } else {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    try {
      const copiedOk = await copyChartAsImage(chartElement, chart.title, { skipScroll: true });
      if (copiedOk) {
        setCopiedChartId(`chart-${chartIndex}`);
        success("Chart copied", `"${chart.title}" copied to clipboard as an image.`);
        setTimeout(() => setCopiedChartId(null), 2000);
      } else {
        success("Copy failed", "Failed to copy chart. The image has been downloaded instead.");
      }
    } finally {
      setCopyingChartIndex(null);
    }
  };

  const handleCopyTableAsImage = async (chart: ChartData, chartIndex: number) => {
    setCopyingTableIndex(chartIndex);
    const displayData = chart.fullData && chart.fullData.length > 0 ? chart.fullData : chart.data;
    if (!displayData || displayData.length === 0) {
      success("Error", "No data available to copy.");
      setCopyingTableIndex(null);
      return;
    }
    const allKeys = Object.keys(displayData[0] || {});
    const formatFieldName = (key: string) =>
      chart.metric_display_names?.[key] ?? stripChartPrefix(key).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    const formatCellValue = (value: unknown, column: string): string => {
      if (value == null) return '-';
      if (typeof value === 'number') return formatNumberFull(value, chart.metric_formats?.[column]);
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    try {
      const scale = 2;
      const padding = 16 * scale;
      const cellPaddingX = 12 * scale;
      const headerHeight = 40 * scale;
      const rowHeight = 36 * scale;
      const titleBaseHeight = 34 * scale;
      const titleLineHeight = 22 * scale;
      const fontSize = 13 * scale;
      const headerFontSize = 12 * scale;
      const titleFontSize = 16 * scale;
      const borderWidth = 1 * scale;
      const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

      const measureCanvas = document.createElement('canvas');
      const measureCtx = measureCanvas.getContext('2d')!;
      measureCtx.font = `bold ${headerFontSize}px ${font}`;

      const columnWidths: number[] = allKeys.map((key) => {
        const headerText = formatFieldName(key);
        let maxWidth = measureCtx.measureText(headerText).width + cellPaddingX * 2;
        measureCtx.font = `${fontSize}px ${font}`;
        displayData.forEach((row: Record<string, unknown>) => {
          const cellWidth = measureCtx.measureText(formatCellValue(row[key], key)).width + cellPaddingX * 2;
          maxWidth = Math.max(maxWidth, cellWidth);
        });
        return Math.min(Math.max(maxWidth, 80 * scale), 300 * scale);
      });

      const minTableWidth = 640 * scale;
      let tableWidth = columnWidths.reduce((sum, w) => sum + w, 0) + borderWidth;
      if (tableWidth < minTableWidth) {
        const perColumnExtra = (minTableWidth - tableWidth) / columnWidths.length;
        columnWidths.forEach((_, idx) => { columnWidths[idx] += perColumnExtra; });
        tableWidth = minTableWidth;
      }

      measureCtx.font = `bold ${titleFontSize}px ${font}`;
      const titleWords = String(chart.title || '').split(/\s+/).filter(Boolean);
      const titleLines: string[] = [];
      let currentLine = '';
      for (const w of titleWords) {
        const testLine = currentLine ? `${currentLine} ${w}` : w;
        if (measureCtx.measureText(testLine).width <= tableWidth - cellPaddingX * 2) {
          currentLine = testLine;
        } else {
          if (currentLine) titleLines.push(currentLine);
          currentLine = w;
        }
      }
      if (currentLine) titleLines.push(currentLine);
      const titleHeight = titleBaseHeight + Math.max(1, titleLines.length) * titleLineHeight;

      const tableHeight = titleHeight + headerHeight + displayData.length * rowHeight + borderWidth;
      const canvas = document.createElement('canvas');
      canvas.width = tableWidth + padding * 2;
      canvas.height = tableHeight + padding * 2;
      const ctx = canvas.getContext('2d')!;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#1f2937';
      ctx.font = `bold ${titleFontSize}px ${font}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      titleLines.forEach((line, idx) => ctx.fillText(line, canvas.width / 2, padding + titleBaseHeight / 2 + idx * titleLineHeight));

      const tableX = padding;
      const tableY = padding + titleHeight;
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = borderWidth;
      ctx.strokeRect(tableX, tableY, tableWidth, tableHeight - titleHeight);
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(tableX, tableY, tableWidth, headerHeight);

      ctx.fillStyle = '#374151';
      ctx.font = `bold ${headerFontSize}px ${font}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      let xPos = tableX;
      allKeys.forEach((key, colIdx) => {
        ctx.fillText(formatFieldName(key), xPos + cellPaddingX, tableY + headerHeight / 2, columnWidths[colIdx] - cellPaddingX * 2);
        if (colIdx < allKeys.length - 1) {
          ctx.strokeStyle = '#e5e7eb';
          ctx.beginPath();
          ctx.moveTo(xPos + columnWidths[colIdx], tableY);
          ctx.lineTo(xPos + columnWidths[colIdx], tableY + tableHeight - titleHeight);
          ctx.stroke();
        }
        xPos += columnWidths[colIdx];
      });
      ctx.strokeStyle = '#d1d5db';
      ctx.beginPath();
      ctx.moveTo(tableX, tableY + headerHeight);
      ctx.lineTo(tableX + tableWidth, tableY + headerHeight);
      ctx.stroke();

      ctx.font = `${fontSize}px ${font}`;
      displayData.forEach((row: Record<string, unknown>, rowIdx: number) => {
        const rowY = tableY + headerHeight + rowIdx * rowHeight;
        if (rowIdx % 2 === 1) {
          ctx.fillStyle = '#f9fafb';
          ctx.fillRect(tableX + borderWidth / 2, rowY, tableWidth - borderWidth, rowHeight);
        }
        ctx.strokeStyle = '#f3f4f6';
        ctx.beginPath(); ctx.moveTo(tableX, rowY + rowHeight); ctx.lineTo(tableX + tableWidth, rowY + rowHeight); ctx.stroke();
        let cellX = tableX;
        allKeys.forEach((key, colIdx) => {
          const cellValue = row[key];
          const displayValue = formatCellValue(cellValue, key);
          const isNumber = typeof cellValue === 'number';
          ctx.fillStyle = '#1f2937';
          ctx.textAlign = isNumber ? 'right' : 'left';
          const textX = isNumber ? cellX + columnWidths[colIdx] - cellPaddingX : cellX + cellPaddingX;
          ctx.fillText(displayValue, textX, rowY + rowHeight / 2, columnWidths[colIdx] - cellPaddingX * 2);
          cellX += columnWidths[colIdx];
        });
      });

      canvas.toBlob(async (blob) => {
        if (!blob) { success("Copy failed", "Failed to generate table image."); return; }
        try {
          if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            success("Table copied", "The table has been copied as an image.");
          } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${chart.title.replace(/[^a-z0-9]/gi, '_')}_table.png`;
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            URL.revokeObjectURL(url);
            success("Table downloaded", "The table has been downloaded as an image.");
          }
        } catch {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${chart.title.replace(/[^a-z0-9]/gi, '_')}_table.png`;
          document.body.appendChild(link); link.click(); document.body.removeChild(link);
          URL.revokeObjectURL(url);
          success("Table downloaded", "Clipboard not available. The table has been downloaded.");
        }
      }, 'image/png');
    } catch {
      success("Copy failed", "An error occurred while generating the table image.");
    } finally {
      setCopyingTableIndex(null);
    }
  };

  const chartItems = charts.map((chart, index) => ({ chart, index, key: getChartCardKey(chart, index) }));
  const visibleChartItems = chartItems.filter(({ key }) => !hiddenCharts.has(key));
  const hiddenChartItems = chartItems.filter(({ key }) => hiddenCharts.has(key));
  const visibleHiddenChartItems = hiddenChartItems.slice(0, 2);
  const overflowHiddenChartItems = hiddenChartItems.slice(2);
  // Repo-2 branding pass: respond to the actual dashboard canvas width, not
  // viewport breakpoints, so expanded app nav + docked chat cannot squeeze
  // two chart cards into a narrow remaining panel.
  // Tighter inter-card gap (was gap-4 / 16px) — gives each chart
  // ~6px more real estate per side and keeps the canvas dense without
  // making cards feel glued together.
  const chartGridClassName = cn(
    "relative grid min-w-0 grid-cols-1 gap-2.5 transition-all duration-500 ease-in-out"
  );
  // Drop the 34rem (544px) min-width per column to 30rem (480px) so
  // a docked chat panel doesn't force charts into a single-column
  // stack on wide canvases. Two-column layout kicks in earlier.
  const chartGridStyle = visibleChartItems.length <= 1
    ? undefined
    : { gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 30rem), 1fr))' };

  return (
    <>
      {hiddenChartItems.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[12px] border border-[#EDE6E1] bg-[#FAF7F1]/80 px-3 py-2">
          <span className="text-xs font-medium text-[#7B6B72]">Hidden charts. Use these to restore them.</span>
          {visibleHiddenChartItems.map(({ chart, key }) => (
            <TooltipProvider key={key} delayDuration={150}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(chartCompactButtonClassName, "max-w-[280px] gap-1.5 rounded-full")}
                    onClick={() => {
                      setHiddenCharts((prev) => {
                        const next = new Set(prev);
                        next.delete(key);
                        return next;
                      });
                    }}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    <span className="max-w-[220px] truncate">{chart.title}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" sideOffset={8} className="max-w-sm rounded-[10px] border border-[#EDE6E1] bg-white text-[#1F1A20] shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]">
                  {chart.title}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          ))}
          {overflowHiddenChartItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(chartCompactButtonClassName, "rounded-full")}
                >
                  {overflowHiddenChartItems.length} more
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className={cn(chartMenuContentClassName, "w-[280px]")}>
                <DropdownMenuItem
                  className={cn(chartMenuItemClassName, "gap-2 font-medium")}
                  onClick={() => {
                    setHiddenCharts(new Set());
                  }}
                >
                  <Eye className="h-3.5 w-3.5 shrink-0" />
                  <span>Show all hidden charts</span>
                </DropdownMenuItem>
                <div className="mx-2 my-1 h-px bg-[#EDE6E1]" />
                {overflowHiddenChartItems.map(({ chart, key }) => (
                  <DropdownMenuItem
                    key={key}
                    className={cn(chartMenuItemClassName, "gap-2")}
                    onClick={() => {
                      setHiddenCharts((prev) => {
                        const next = new Set(prev);
                        next.delete(key);
                        return next;
                      });
                    }}
                  >
                    <Eye className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{chart.title}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      <div className={chartGridClassName} style={chartGridStyle}>
        {visibleChartItems.map(({ chart, index, key: chartKey }) => {
          const effectiveChart = chartFromTemplate(chart);
          const fullData = effectiveChart.fullData || effectiveChart.data || [];
          const dataLen = fullData.length;
          const isBarOrLineLike = ['bar', 'line', 'area', 'multi_column_bar', 'stacked_bar', 'horizontal_bar'].includes(effectiveChart.type);
          const isHorizontalBarLike = effectiveChart.type === 'horizontal_bar' || effectiveChart.type === 'diverging_bar';
          const defaultTopN = isHorizontalBarLike ? HORIZONTAL_BAR_TOP_X_VALUES : TOP_X_VALUES;
          const hasMoreThanDefaultTopN = isBarOrLineLike && dataLen > defaultTopN;
          const hasTopNChoices = isBarOrLineLike && dataLen > 3;
          const isLimitExpanded = expandedDataCharts.has(chartKey);
          const isFullWidth = fullWidthCharts.has(chartKey);
          const isTableView = showDataTables.has(chartKey);
          const selectedTopN = chartTopNMode[chartKey];
          const topNOptions = getChartTopNOptions(effectiveChart, dataLen);
          const canViewChart = isChartActionEnabled(chart, 'view_chart');
          const canViewTable = isChartActionEnabled(chart, 'view_table');
          const canSort = isChartActionEnabled(chart, 'sort');
          const canUseTopN = isChartActionEnabled(chart, 'top_n');
          const canReset = isChartActionEnabled(chart, 'reset');
          const canDownloadCSV = isChartActionEnabled(chart, 'download_csv');
          const canCopyChart = isChartActionEnabled(chart, 'copy_chart');
          const canExpand = isChartActionEnabled(chart, 'expand');
          const hasPopOutAction = chart.template?.actions?.some((action: ChartActionConfig) => action.id === 'pop_out') ?? false;
          const canPopOut = isChartActionEnabled(chart, 'pop_out') || (!hasPopOutAction && canExpand);
          const canDrillDown = isChartActionEnabled(chart, 'drill_down');
          const canHide = isChartActionEnabled(chart, 'hide');
          const canCopyTable = isChartActionEnabled(chart, 'copy_table');
          const canCopyTableImage = isChartActionEnabled(chart, 'copy_table_image');
          const showRowLayoutButton = canExpand && canPopOut;
          const sortMode = chartSortMode[chartKey] ?? 'none';
          const topNToApply = selectedTopN ?? (hasMoreThanDefaultTopN && !isLimitExpanded ? defaultTopN : 'all');
          const appliedTopNLabel = formatTopNLabel(topNToApply);
          const useTopNDropdown = topNOptions.length > 5;
          const sourceChartData = fullData.length > 0 ? fullData : (effectiveChart.data || []);
          const backendSortedChartData = sortChartDataByField(
            sourceChartData as Record<string, unknown>[],
            getBackendSortField(effectiveChart, sourceChartData as Record<string, unknown>[]),
            getBackendSortOrder(effectiveChart)
          );
          const sortedSourceChartData = sortMode === 'none'
            ? backendSortedChartData
            : sortChartData(effectiveChart, backendSortedChartData, sortMode);
          const renderedChartData = topNToApply === 'all'
            ? sortedSourceChartData
            : takeFirstInOriginalOrder(sortedSourceChartData, topNToApply);
          const chartForDataActions: ChartData = {
            ...effectiveChart,
            data: renderedChartData,
            fullData: renderedChartData,
          };
          const renderedDataLen = renderedChartData.length;
          const isRenderedFullWidth = isFullWidth;
          const isMatrixChart = effectiveChart.type === 'matrix';
          const horizontalLabelGutter = getHorizontalBarLabelGutter(
            effectiveChart,
            renderedChartData as Record<string, unknown>[],
            isRenderedFullWidth
          );
          const chartMinWidth = getChartMinWidth(effectiveChart, renderedDataLen, isRenderedFullWidth)
            + Math.max(0, horizontalLabelGutter - HORIZONTAL_BAR_LABEL_GUTTER_MIN);
          const chartViewportHeight = getChartViewportHeight(effectiveChart, renderedDataLen, isRenderedFullWidth);
          const chartRenderHeight = getAdjustedChartRenderHeight(effectiveChart, renderedChartData as Record<string, unknown>[], isRenderedFullWidth);
          const formatFieldName = (key: string) =>
            effectiveChart.metric_display_names?.[key] ?? stripChartPrefix(key).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
          const openChartPopOut = () => {
            setExpandedChartContext({
              chart: {
                ...effectiveChart,
                data: renderedChartData,
                // Hand the modal the complete source rows (not just the
                // top-N currently rendered inline) so its own "All" toggle
                // can expand to every available row instead of being stuck
                // at the inline cap. ``sourceChartData`` is the full set the
                // chart was built from (capped to max_chart_rows upstream).
                fullData: sortedSourceChartData as Record<string, unknown>[],
                template: effectiveChart.template
                  ? { ...effectiveChart.template, data: renderedChartData }
                  : undefined,
              },
              data: renderedChartData as Record<string, unknown>[],
              originalRowCount: sortedSourceChartData.length,
              appliedTopN: topNToApply,
              sortLabel: formatSortModeLabel(sortMode),
            });
          };
          const setTopNOption = (option: ChartTopNValue) => {
            setChartTopNMode((prev) => ({ ...prev, [chartKey]: option }));
            setExpandedDataCharts((prev) => {
              const next = new Set(prev);
              if (option === 'all') next.add(chartKey);
              else next.delete(chartKey);
              return next;
            });
            setFocusTargetChartKey(chartKey);
          };
          const resetChartView = () => {
            setChartSortMode((prev) => {
              const next = { ...prev };
              delete next[chartKey];
              return next;
            });
            setExpandedDataCharts((prev) => {
              const next = new Set(prev);
              next.delete(chartKey);
              return next;
            });
            setChartTopNMode((prev) => {
              const next = { ...prev };
              delete next[chartKey];
              return next;
            });
            setShowDataTables((prev) => {
              const next = new Set(prev);
              next.delete(chartKey);
              return next;
            });
            setFullWidthCharts((prev) => {
              const next = new Set(prev);
              next.delete(chartKey);
              return next;
            });
          };
          const toggleFullWidth = () => {
            setFullWidthCharts((prev) => {
              const next = new Set(prev);
              if (next.has(chartKey)) next.delete(chartKey);
              else next.add(chartKey);
              return next;
            });
            setFocusTargetChartKey(chartKey);
          };
          const hideChart = () => {
            setHiddenCharts((prev) => {
              const next = new Set(prev);
              next.add(chartKey);
              return next;
            });
          };

          return (
            <FinanceV2Section
              key={chartKey}
              ref={setCardRef(chartKey)}
              tabIndex={-1}
              className={chartSectionClassName}
              style={isFullWidth ? { gridColumn: '1 / -1' } : undefined}
              title={
                <span className="inline-flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5 text-[#4E7C89]" />
                  Visualization · {formatChartTypeLabel(effectiveChart.type)}
                </span>
              }
            >
              <div className="mb-2.5 min-w-0">
                {chart.reasoning ? (
                  <TooltipProvider delayDuration={300}>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <h4 className="cursor-help font-display text-[20px] font-normal leading-[1.15] text-[#1F1A20]">
                          {effectiveChart.title}
                        </h4>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start" sideOffset={8} className="max-w-sm rounded-[10px] border border-[#EDE6E1] bg-white px-4 py-3 text-sm text-[#3F3338] shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]">
                        <p className="leading-relaxed">{chart.reasoning}</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                ) : (
                  <h4 className="font-display text-[20px] font-normal leading-[1.15] text-[#1F1A20]">{effectiveChart.title}</h4>
                )}
              </div>

              {/* Toolbar ribbon — tightened from mb-4/px-3/py-2 to
                  mb-2/px-2/py-1.5. Saves ~14px of vertical chrome per
                  chart card without crowding the buttons. */}
              <div className="mb-2 rounded-[10px] border border-[#EDE6E1] bg-[#FAF7F1]/70 px-2 py-1.5 backdrop-blur-[2px]">
                <ChartToolbarSurface>
                  {(toolbarWidth) => {
                    // V2-only: overflow chart actions based on measured toolbar width,
                    // not on whether the app nav/chat bars are currently collapsed.
                    const showInlineUtilityActions = toolbarWidth !== null && toolbarWidth >= CHART_INLINE_ACTIONS_WIDTH;
                    const showInlineControlActions = toolbarWidth !== null && toolbarWidth >= CHART_INLINE_CONTROLS_WIDTH;
                    const menuHasSort = isBarOrLineLike && canSort && !showInlineControlActions;
                    const menuHasTopN = hasTopNChoices && canUseTopN && !showInlineControlActions;
                    const menuHasReset = canReset && !showInlineUtilityActions;
                    const menuHasDownloadCSV = canDownloadCSV && !showInlineUtilityActions;
                    const menuHasCopyChart = !isTableView && canCopyChart && !showInlineUtilityActions;
                    const menuHasRowLayout = showRowLayoutButton && !showInlineUtilityActions;
                    const menuHasDrillDown = canDrillDown && !showInlineUtilityActions;
                    const menuHasHide = canHide && !showInlineUtilityActions;
                    const hasMenuActions =
                      menuHasSort ||
                      menuHasTopN ||
                      menuHasReset ||
                      menuHasDownloadCSV ||
                      menuHasCopyChart ||
                      menuHasRowLayout ||
                      menuHasDrillDown ||
                      menuHasHide;

                    return (
                  <>
                    {(canViewChart || canViewTable) && (
                      <div className="inline-flex items-center rounded-[8px] border border-[#EDE6E1] bg-white p-[2px]">
                        <button
                          type="button"
                          disabled={!canViewChart}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDataTables((prev) => {
                              const next = new Set(prev);
                              next.delete(chartKey);
                              return next;
                            });
                            setFocusTargetChartKey(chartKey);
                          }}
                          className={cn(
                            "inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                            !isTableView ? "bg-[#6E3450] text-white" : "text-[#7B6B72] hover:text-[#1F1A20]"
                          )}
                          title="View as chart"
                          aria-label="View as chart"
                        >
                          <BarChart3 className="h-3.5 w-3.5" />
                          <span>Chart</span>
                        </button>
                        <button
                          type="button"
                          disabled={!canViewTable}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDataTables((prev) => {
                              const next = new Set(prev);
                              next.add(chartKey);
                              return next;
                            });
                            setFocusTargetChartKey(chartKey);
                          }}
                          className={cn(
                            "inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                            isTableView ? "bg-[#6E3450] text-white" : "text-[#7B6B72] hover:text-[#1F1A20]"
                          )}
                          title="View as table"
                          aria-label="View as table"
                        >
                          <TableIcon className="h-3.5 w-3.5" />
                          <span>Table</span>
                        </button>
                      </div>
                    )}
                    {showInlineControlActions && isBarOrLineLike && canSort && (
                      <label className="inline-flex h-8 items-center gap-2">
                        <span className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
                          Sort
                        </span>
                        <select
                          value={sortMode}
                          onChange={(e) => {
                            e.stopPropagation();
                            setChartSortMode((prev) => ({ ...prev, [chartKey]: e.target.value as ChartSortMode }));
                            setFocusTargetChartKey(chartKey);
                          }}
                          className="h-8 rounded-[8px] border border-[#EDE6E1] bg-white px-3 text-[12px] text-[#1F1A20] outline-none transition-colors hover:border-[#6E3450]/35 focus:border-[#6E3450]"
                          title="Sort data"
                        >
                          {CHART_SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    {showInlineControlActions && hasTopNChoices && canUseTopN && (
                      useTopNDropdown ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={chartCompactButtonClassName}
                              onClick={(e) => e.stopPropagation()}
                              title="Choose how many values to show"
                            >
                              {appliedTopNLabel}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className={chartMenuContentClassName} onClick={(e) => e.stopPropagation()}>
                            {topNOptions.map((option) => (
                              <DropdownMenuItem
                                key={String(option)}
                                className={cn(chartMenuItemClassName, option === topNToApply && "bg-[#eef6fa] text-[#4E7C89]")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTopNOption(option);
                                }}
                              >
                                {formatTopNLabel(option)}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div className="inline-flex h-7 items-center overflow-hidden rounded-[8px] border border-[#EDE6E1] bg-white">
                          {topNOptions.map((option) => {
                            const active = option === topNToApply;
                            return (
                              <button
                                key={String(option)}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTopNOption(option);
                                }}
                                className={cn(
                                  "h-full border-r border-[#EDE6E1] px-2 text-[11px] font-medium transition-colors last:border-r-0",
                                  active
                                    ? "bg-[#4E7C89] text-white"
                                    : "text-[#7B6B72] hover:bg-[#FAF7F1] hover:text-[#1F1A20]"
                                )}
                                title={option === 'all' ? 'Show all values' : `Show top ${option} values`}
                              >
                                {formatTopNLabel(option)}
                              </button>
                            );
                          })}
                        </div>
                      )
                    )}
                    <span className="ml-auto text-[12px] italic text-[#7B6B72]">
                      Showing <b className="not-italic text-[#1F1A20] tabular-nums">{renderedDataLen}</b>
                    </span>
                    {showInlineUtilityActions && canReset && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={chartIconButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          resetChartView();
                        }}
                        title="Reset chart view"
                        aria-label="Reset chart view"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {showInlineUtilityActions && canDownloadCSV && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={chartIconButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadCSV(chartForDataActions);
                        }}
                        title="Download CSV"
                        aria-label="Download CSV"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {showInlineUtilityActions && !isTableView && canCopyChart && (
                      <Button
                        variant={copiedChartId === `chart-${index}` ? 'secondary' : 'ghost'}
                        size="sm"
                        className={copiedChartId === `chart-${index}` ? chartActiveIconButtonClassName : chartIconButtonClassName}
                        disabled={copyingChartIndex === index}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (copyingChartIndex == null) handleCopyChart(effectiveChart, index);
                        }}
                        title={copyingChartIndex === index ? 'Copying chart image...' : 'Copy chart image'}
                        aria-label={copyingChartIndex === index ? 'Copying chart image...' : 'Copy chart image'}
                      >
                        {copyingChartIndex === index ? (
                          <Spinner className="h-3.5 w-3.5 text-[#6E3450]" />
                        ) : copiedChartId === `chart-${index}` ? (
                          <CheckCircle className="h-3.5 w-3.5 text-[#6E3450]" />
                        ) : (
                          <ImageIcon className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                    {showInlineUtilityActions && showRowLayoutButton && (
                      <Button
                        variant={isFullWidth ? 'secondary' : 'ghost'}
                        size="sm"
                        className={isFullWidth ? chartActiveIconButtonClassName : chartIconButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFullWidth();
                        }}
                        title={isFullWidth ? 'Return chart to grid' : 'Use full row'}
                        aria-label={isFullWidth ? 'Return chart to grid' : 'Use full row'}
                      >
                        {isFullWidth ? <FoldHorizontal className="h-3.5 w-3.5" /> : <UnfoldHorizontal className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {showInlineUtilityActions && canDrillDown && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={chartIconButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChartInfoClick(chart);
                        }}
                        title="Open chart details"
                        aria-label="Open chart details"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {showInlineUtilityActions && canHide && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={chartIconButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          hideChart();
                        }}
                        title={isTableView ? 'Hide table' : 'Hide chart'}
                        aria-label={isTableView ? 'Hide table' : 'Hide chart'}
                      >
                        <EyeOff className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {(canPopOut || canExpand) && (
                      <Button
                        variant="default"
                        size="sm"
                        className="inline-flex h-8 items-center gap-1.5 rounded-[8px] bg-[#1F1A20] px-3 text-[12px] font-medium text-white shadow-none transition-colors hover:bg-[#3F3338]"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canPopOut) {
                            openChartPopOut();
                            return;
                          }
                          if (canExpand) {
                            toggleFullWidth();
                          }
                        }}
                        title={canPopOut ? 'Expand chart details' : isFullWidth ? 'Collapse chart row' : 'Expand chart to full row'}
                        aria-label={canPopOut ? 'Expand chart details' : isFullWidth ? 'Collapse chart row' : 'Expand chart to full row'}
                      >
                        {canPopOut ? (
                          <Maximize2 className="h-3.5 w-3.5" />
                        ) : isFullWidth ? (
                          <FoldHorizontal className="h-3.5 w-3.5" />
                        ) : (
                          <UnfoldHorizontal className="h-3.5 w-3.5" />
                        )}
                        {canPopOut ? 'Expand' : isFullWidth ? 'Collapse' : 'Expand'}
                      </Button>
                    )}
                    {hasMenuActions && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={chartIconButtonClassName}
                            onClick={(e) => e.stopPropagation()}
                            title="More chart actions"
                            aria-label="More chart actions"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className={cn(chartMenuContentClassName, "w-[260px]")} onClick={(e) => e.stopPropagation()}>
                          {menuHasSort && (
                            <>
                              <DropdownMenuLabel className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
                                Sort
                              </DropdownMenuLabel>
                              {CHART_SORT_OPTIONS.map((option) => {
                                const active = sortMode === option.value;
                                return (
                                  <DropdownMenuItem
                                    key={option.value}
                                    className={cn(chartMenuItemClassName, "flex items-center gap-2", active && "bg-[#f5e9ee] text-[#6E3450]")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setChartSortMode((prev) => ({ ...prev, [chartKey]: option.value }));
                                      setFocusTargetChartKey(chartKey);
                                    }}
                                  >
                                    <span className={cn("h-2 w-2 rounded-full", active ? "bg-[#6E3450]" : "bg-transparent")} />
                                    {option.label}
                                  </DropdownMenuItem>
                                );
                              })}
                              <DropdownMenuSeparator className="bg-[#EDE6E1]" />
                            </>
                          )}
                          {menuHasTopN && (
                            <>
                              <DropdownMenuLabel className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
                                Show values
                              </DropdownMenuLabel>
                              {topNOptions.map((option) => {
                                const active = option === topNToApply;
                                return (
                                  <DropdownMenuItem
                                    key={String(option)}
                                    className={cn(chartMenuItemClassName, "flex items-center gap-2", active && "bg-[#eef6fa] text-[#4E7C89]")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTopNOption(option);
                                    }}
                                  >
                                    <span className={cn("h-2 w-2 rounded-full", active ? "bg-[#4E7C89]" : "bg-transparent")} />
                                    {formatTopNLabel(option)}
                                  </DropdownMenuItem>
                                );
                              })}
                              <DropdownMenuSeparator className="bg-[#EDE6E1]" />
                            </>
                          )}
                          {menuHasReset && (
                            <DropdownMenuItem
                              className={cn(chartMenuItemClassName, "flex items-center gap-2")}
                              onClick={(e) => {
                                e.stopPropagation();
                                resetChartView();
                              }}
                            >
                              <RotateCcw className="h-3.5 w-3.5 text-[#7B6B72]" />
                              Reset view
                            </DropdownMenuItem>
                          )}
                          {menuHasDownloadCSV && (
                            <DropdownMenuItem
                              className={cn(chartMenuItemClassName, "flex items-center gap-2")}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadCSV(chartForDataActions);
                              }}
                            >
                              <Download className="h-3.5 w-3.5 text-[#7B6B72]" />
                              Download CSV
                            </DropdownMenuItem>
                          )}
                          {menuHasCopyChart && (
                            <DropdownMenuItem
                              disabled={copyingChartIndex === index}
                              className={cn(chartMenuItemClassName, "flex items-center gap-2")}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (copyingChartIndex == null) handleCopyChart(effectiveChart, index);
                              }}
                            >
                              {copyingChartIndex === index ? (
                                <Spinner className="h-3.5 w-3.5 text-[#6E3450]" />
                              ) : copiedChartId === `chart-${index}` ? (
                                <CheckCircle className="h-3.5 w-3.5 text-[#6E3450]" />
                              ) : (
                                <ImageIcon className="h-3.5 w-3.5 text-[#7B6B72]" />
                              )}
                              {copyingChartIndex === index ? 'Copying image...' : 'Copy chart image'}
                            </DropdownMenuItem>
                          )}
                          {menuHasRowLayout && (
                            <DropdownMenuItem
                              className={cn(chartMenuItemClassName, "flex items-center gap-2")}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFullWidth();
                              }}
                            >
                              {isFullWidth ? (
                                <FoldHorizontal className="h-3.5 w-3.5 text-[#7B6B72]" />
                              ) : (
                                <UnfoldHorizontal className="h-3.5 w-3.5 text-[#7B6B72]" />
                              )}
                              {isFullWidth ? 'Return to grid' : 'Use full row'}
                            </DropdownMenuItem>
                          )}
                          {menuHasDrillDown && (
                            <DropdownMenuItem
                              className={cn(chartMenuItemClassName, "flex items-center gap-2")}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChartInfoClick(chart);
                              }}
                            >
                              <Info className="h-3.5 w-3.5 text-[#7B6B72]" />
                              Open chart details
                            </DropdownMenuItem>
                          )}
                          {menuHasHide && (
                            <DropdownMenuItem
                              className={cn(chartMenuItemClassName, "flex items-center gap-2")}
                              onClick={(e) => {
                                e.stopPropagation();
                                hideChart();
                              }}
                            >
                              <EyeOff className="h-3.5 w-3.5 text-[#7B6B72]" />
                              {isTableView ? 'Hide table' : 'Hide chart'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </>
                    );
                  }}
                </ChartToolbarSurface>
                </div>
                {showDataTables.has(chartKey) ? (() => {
                  const displayData = renderedChartData;
                  if (!displayData || displayData.length === 0) return null;
                  // Extract all columns from all rows, not just the first row
                  // (first row might be sparse and miss metric columns)
                  const allKeysSet = new Set<string>();
                  displayData.forEach((row: Record<string, unknown>) => {
                    Object.keys(row).forEach(key => allKeysSet.add(key));
                  });
                  const allKeys = Array.from(allKeysSet);
                  return (
                    <div data-table-id={`table-${index}`} data-table-title={effectiveChart.title}>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h4 className="font-display text-sm font-semibold text-[#1F1A20]">Chart Data</h4>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-[#7B6B72]">{displayData.length} {displayData.length === 1 ? 'row' : 'rows'}</span>
                          {canCopyTable && (
                            <Button variant="outline" size="sm" className={chartCompactButtonClassName}
                              onClick={async (e) => {
                                e.stopPropagation();
                                const ok = await copyTextToClipboard(buildTSV(displayData, allKeys));
                                success(ok ? 'Table copied' : 'Copy failed', ok ? 'The full table has been copied to your clipboard.' : 'Could not copy the table.');
                              }}
                              title="Copy full table to clipboard">Copy Table</Button>
                          )}
                          {canCopyTableImage && (
                            <Button variant="outline" size="sm" className={chartCompactButtonClassName}
                              onClick={async (e) => { e.stopPropagation(); if (copyingTableIndex == null) await handleCopyTableAsImage(chartForDataActions, index); }}
                              disabled={copyingTableIndex === index}
                              title={copyingTableIndex === index ? "Copying table..." : "Copy the table as an image"}>
                              {copyingTableIndex === index ? <Spinner className="mr-1 h-3.5 w-3.5 text-[#6E3450]" /> : null}
                              <span>Copy Image</span>
                            </Button>
                          )}
                          {canDownloadCSV && (
                            <Button variant="outline" size="sm" className={chartCompactButtonClassName}
                              onClick={(e) => { e.stopPropagation(); handleDownloadCSV(chartForDataActions); }}
                              title="Download table as CSV">CSV</Button>
                          )}
                        </div>
                      </div>
                      {/* Repo-2 branding pass: shared v2 table surface keeps chart/card table styling aligned. */}
                      <FinanceV2DataTable
                        rows={displayData as Record<string, unknown>[]}
                        columns={allKeys}
                        density={isFullWidth ? 'comfortable' : 'compact'}
                        // Width-independent. Full-width gives the table
                        // more room for columns; it must not also grow
                        // taller. Rows beyond what fits scroll inside.
                        maxHeight="420px"
                        getColumnLabel={formatFieldName}
                        formatCell={(cellValue, column) => cellValue == null ? '-'
                          : typeof cellValue === 'number' ? formatNumberFull(cellValue, effectiveChart.metric_formats?.[column])
                          : typeof cellValue === 'object' ? JSON.stringify(cellValue)
                          : String(cellValue)}
                      />
                    </div>
                  );
                })() : (() => {
                  const dataToUse = renderedChartData;
	                  const chartToRender = {
	                    ...effectiveChart,
	                    data: dataToUse,
	                    template: effectiveChart.template ? { ...effectiveChart.template, data: dataToUse } : undefined,
	                  };
	                  const numericFields = getChartNumericFields(effectiveChart, dataToUse as Record<string, unknown>[]);
	                  const isFrozenVertical = supportsFrozenVerticalAxis(effectiveChart.type);
	                  const isFrozenHorizontal = supportsFrozenHorizontalAxis(effectiveChart.type);

	                  if (isFrozenVertical) {
	                    const leftFields = effectiveChart.type === 'dual_axis_line'
	                      ? numericFields.slice(0, Math.ceil(numericFields.length / 2))
	                      : numericFields;
	                    const rightFields = effectiveChart.type === 'dual_axis_line'
	                      ? numericFields.slice(Math.ceil(numericFields.length / 2))
	                      : [];
                    const leftRange = getChartValueRange(dataToUse as Record<string, unknown>[], leftFields);
                    const rightRange = rightFields.length > 0
                      ? getChartValueRange(dataToUse as Record<string, unknown>[], rightFields)
                      : null;
                    const leftTicks = getNiceTickValues(leftRange.min, leftRange.max, 5);
                    const rightTicks = rightRange ? getNiceTickValues(rightRange.min, rightRange.max, 5) : [];
                    const verticalGrid = getCartesianChartGrid(effectiveChart, dataToUse as Record<string, unknown>[], numericFields);
                    const axisPlotHeight = Math.max(80, chartRenderHeight - verticalGrid.top - verticalGrid.bottom);

                    return (
                      <div className="flex overflow-hidden rounded-[10px] border border-[#EDE6E1] bg-white">
                        <div
                          className="shrink-0 border-r border-[#EDE6E1] bg-[#FAF7F1]/70"
                          style={{ width: `${FROZEN_AXIS_LEFT_WIDTH}px`, height: `${chartRenderHeight}px` }}
                        >
                          <div style={{ height: `${verticalGrid.top}px` }} />
                          <div
                            className="flex flex-col justify-between px-2 text-[11px] text-[#7B6B72]"
                            style={{ height: `${axisPlotHeight}px` }}
                          >
                            {leftTicks.slice().reverse().map((tick) => (
                              <span key={tick} className="text-right tabular-nums leading-none">
                                {formatNumberShort(tick)}
                              </span>
                            ))}
                          </div>
	                          <div className="flex items-center justify-center px-2 text-center text-[11px] text-[#7B6B72]" style={{ height: `${verticalGrid.bottom}px` }}>
	                            <span className="line-clamp-2">
	                              {effectiveChart.y_axis_label || (leftFields.length === 1 ? getDisplayName(effectiveChart, leftFields[0]) : '')}
	                            </span>
	                          </div>
                        </div>

                        <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden pb-2">
	                          <div
	                            data-chart-id={`chart-${index}`}
	                            data-chart-title={effectiveChart.title}
	                            style={{ width: `${chartMinWidth}px`, minWidth: '100%', minHeight: `${chartRenderHeight}px` }}
	                          >
                            <ChartRenderer
                              chart={chartToRender}
                              height={chartRenderHeight}
                              colors={chartColors}
                              presentation={{
                                enableRangeSlider: true,
                                hideYAxisLabels: true,
                                hideYAxisName: true,
	                                hideRightYAxisLabels: effectiveChart.type === 'dual_axis_line',
	                                gridOverrides: {
	                                  top: verticalGrid.top,
	                                  left: 12,
	                                  right: effectiveChart.type === 'dual_axis_line' ? 12 : 24,
	                                  bottom: verticalGrid.bottom,
	                                },
                              }}
                            />
                          </div>
                        </div>

                        {rightTicks.length > 0 && (
                          <div
                            className="shrink-0 border-l border-[#EDE6E1] bg-[#FAF7F1]/70"
                            style={{ width: `${FROZEN_AXIS_RIGHT_WIDTH}px`, height: `${chartRenderHeight}px` }}
                          >
                            <div style={{ height: `${verticalGrid.top}px` }} />
                            <div
                              className="flex flex-col justify-between px-2 text-[11px] text-[#7B6B72]"
                              style={{ height: `${axisPlotHeight}px` }}
                            >
                              {rightTicks.slice().reverse().map((tick) => (
                                <span key={tick} className="text-left tabular-nums leading-none">
                                  {formatNumberShort(tick)}
                                </span>
                              ))}
                            </div>
	                            <div className="flex items-center justify-center px-2 text-center text-[11px] text-[#7B6B72]" style={{ height: `${verticalGrid.bottom}px` }}>
	                              <span className="line-clamp-2">
	                                {rightFields.length === 1 ? getDisplayName(effectiveChart, rightFields[0]) : ''}
	                              </span>
	                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

	                  if (isFrozenHorizontal) {
                    return (
                      <div className="overflow-hidden rounded-[10px] border border-[#EDE6E1] bg-white">
                        <div
                          className="overflow-x-auto pb-2"
                          style={{ maxHeight: `${chartViewportHeight}px`, overflowY: chartRenderHeight > chartViewportHeight ? 'auto' : 'hidden' }}
                        >
	                          <div
	                            data-chart-id={`chart-${index}`}
	                            data-chart-title={effectiveChart.title}
	                            style={{ width: `${chartMinWidth}px`, minWidth: '100%', minHeight: `${chartRenderHeight}px` }}
	                          >
                            <ChartRenderer
                              chart={chartToRender}
                              height={chartRenderHeight}
                              colors={chartColors}
                              presentation={{
                                enableRangeSlider: true,
	                                gridOverrides: {
                                  left: horizontalLabelGutter,
                                  bottom: 58,
                                },
                                hideYAxisName: true,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (isMatrixChart) {
                    return (
                      <div
                        data-chart-id={`chart-${index}`}
                        data-chart-title={effectiveChart.title}
                        className="min-w-0"
                        // MatrixChart owns horizontal/vertical scrolling internally; avoid a nested outer scrollbar.
                        style={{ minHeight: `${chartRenderHeight}px` }}
                      >
                        <ChartRenderer
                          chart={chartToRender}
                          height={chartRenderHeight}
                          colors={chartColors}
                          presentation={{ enableRangeSlider: true }}
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      className="overflow-x-auto pb-2"
                      style={{ maxHeight: `${chartViewportHeight}px`, overflowY: chartRenderHeight > chartViewportHeight ? 'auto' : 'hidden' }}
                    >
	                      <div
	                        data-chart-id={`chart-${index}`}
	                        data-chart-title={effectiveChart.title}
	                        style={{ width: `${chartMinWidth}px`, minWidth: '100%', minHeight: `${chartRenderHeight}px` }}
	                      >
                        <ChartRenderer
                          chart={chartToRender}
                          height={chartRenderHeight}
                          colors={chartColors}
                          presentation={{ enableRangeSlider: true }}
                        />
                      </div>
                    </div>
                  );
                })()}
            </FinanceV2Section>
          );
        })}
      </div>
      {expandedChartContext && (
        <ChartExpandedModal
          chart={expandedChartContext.chart}
          data={expandedChartContext.data}
          originalRowCount={expandedChartContext.originalRowCount}
          appliedTopN={expandedChartContext.appliedTopN}
          sortLabel={expandedChartContext.sortLabel}
          colors={chartColors}
          onClose={() => setExpandedChartContext(null)}
        />
      )}

    </>
  );
}

export const ChartSection = memo(ChartSectionComponent, (prevProps, nextProps) =>
  prevProps.charts === nextProps.charts &&
  prevProps.botMessageId === nextProps.botMessageId &&
  prevProps.onShowChartLineage === nextProps.onShowChartLineage
);
